import React, { useState } from 'react';
import { CaretDown, CaretRight, CheckCircle, Warning, Prohibit } from '@phosphor-icons/react';
import { Badge } from '../ui/badge';

const FINDING_STYLES = {
  block: { icon: Prohibit, classes: 'text-red-700 bg-red-50 border-red-200' },
  warn: { icon: Warning, classes: 'text-amber-800 bg-amber-50 border-amber-200' },
  pass: { icon: CheckCircle, classes: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
};

const FindingRow = ({ finding }) => {
  const meta = FINDING_STYLES[finding.level] || FINDING_STYLES.warn;
  const Icon = meta.icon;
  return (
    <div
      className={`flex items-start gap-2 text-xs px-2 py-1.5 border rounded ${meta.classes}`}
      data-testid={`preflight-${finding.level}-${finding.rule_id}`}
    >
      <Icon size={14} weight={finding.level === 'pass' ? 'regular' : 'fill'} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <span className="font-mono text-[10px] opacity-70 mr-1.5">{finding.rule_id}</span>
        <span>{finding.message}</span>
        {finding.hint && (
          <p className="text-[11px] opacity-80 mt-0.5 italic">{finding.hint}</p>
        )}
      </div>
    </div>
  );
};

/**
 * Per-claim pre-flight check details: collapsible row showing every
 * Phase B static-rule finding (block / warn / pass) for one claim.
 */
const PreflightFindings = ({ preflight, claimId }) => {
  const [open, setOpen] = useState(false);
  if (!preflight) return null;
  const blockers = preflight.blockers || [];
  const warnings = preflight.warnings || [];
  const passes = preflight.passes || [];
  const hasIssues = blockers.length + warnings.length > 0;

  if (!hasIssues && passes.length === 0) return null;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900"
        data-testid={`preflight-toggle-${claimId}`}
      >
        {open ? <CaretDown size={11} /> : <CaretRight size={11} />}
        Pre-flight checks
        {blockers.length > 0 && (
          <Badge className="bg-red-100 text-red-800 text-[10px] ml-1 px-1.5 py-0">
            {blockers.length} block
          </Badge>
        )}
        {warnings.length > 0 && (
          <Badge className="bg-amber-100 text-amber-800 text-[10px] ml-1 px-1.5 py-0">
            {warnings.length} warn
          </Badge>
        )}
        {!hasIssues && (
          <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-1 px-1.5 py-0">
            all pass
          </Badge>
        )}
      </button>
      {open && (
        <div className="mt-1.5 space-y-1 pl-3" data-testid={`preflight-details-${claimId}`}>
          {blockers.map((f, i) => <FindingRow key={`b-${i}`} finding={f} />)}
          {warnings.map((f, i) => <FindingRow key={`w-${i}`} finding={f} />)}
          {passes.map((f, i) => <FindingRow key={`p-${i}`} finding={f} />)}
        </div>
      )}
    </div>
  );
};

export default PreflightFindings;
